import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { fetchFavoriteBooks } from '../utils/favoritesAndHistoryApi';
import { useAuth } from './AuthContext';

interface FavoriteBook {
  rl_book_id: number;
  favorited_at?: number;
}

interface FavoritesState {
  favoriteBooks: FavoriteBook[];
  lastUpdated: number;
}

type FavoritesAction =
  | { type: 'SET_FAVORITES'; payload: FavoriteBook[] }
  | { type: 'ADD_FAVORITE'; payload: FavoriteBook }
  | { type: 'REMOVE_FAVORITE'; payload: number }; // rl_book_id

const initialState: FavoritesState = {
  favoriteBooks: [],
  lastUpdated: 0,
};

const FavoritesContext = createContext<{
  state: FavoritesState;
  dispatch: React.Dispatch<FavoritesAction>;
} | undefined>(undefined);

const favoritesReducer = (state: FavoritesState, action: FavoritesAction): FavoritesState => {
  switch (action.type) {
    case 'SET_FAVORITES':
      return { ...state, favoriteBooks: action.payload, lastUpdated: Date.now() };
    case 'ADD_FAVORITE':
      return {
        ...state,
        favoriteBooks: [...state.favoriteBooks, action.payload],
        lastUpdated: Date.now(),
      };
    case 'REMOVE_FAVORITE':
      return {
        ...state,
        favoriteBooks: state.favoriteBooks.filter((book) => book.rl_book_id !== action.payload),
        lastUpdated: Date.now(),
      };
    default:
      return state;
  }
};

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(favoritesReducer, initialState);
  const { idToken } = useAuth();

  useEffect(() => {
    const loadFavorites = async () => {
      if (idToken) {
        try {
          const res = await fetchFavoriteBooks(idToken);
          const items: FavoriteBook[] = (res.data?.items ?? []).map((item: any) => ({
            rl_book_id: item.rl_book_id,
            favorited_at: item.favorited_at,
          }));
          dispatch({ type: 'SET_FAVORITES', payload: items });
        } catch (error) {
          console.error('Error fetching favorites:', error);
        }
      }
    };

    loadFavorites();
  }, [idToken]);

  return (
    <FavoritesContext.Provider value={{ state, dispatch }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
